import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './ClubsDashboard.css';
import { GoogleMap, Marker, InfoWindow, useLoadScript } from '@react-google-maps/api';
import { Search, MapPin, Heart, Star, ExternalLink, X, Navigation, Building2, RefreshCw, Compass } from 'lucide-react';
import { clubsService } from '../../../services';
import StateCard from '../../../platform/components/feedback/StateCard';

const FavouriteClubCard = ({ club, onRemove }) => {
    const navigate = useNavigate();

    return (
        <div className="venue-card">
            <div className="venue-card-header">
                <div className="venue-card-icon">
                    <Star size={20} />
                </div>
                <span className="venue-card-badge">Favorite</span>
            </div>
            <h3 className="venue-card-title">{club.name}</h3>
            <p className="venue-card-address">
                <MapPin size={14} />
                {club.address}
            </p>
            <div className="venue-card-actions">
                <button
                    className="venue-btn venue-btn-outline"
                    onClick={() => navigate(`/dashboard/clubs/${club.clubId}`)}
                >
                    <ExternalLink size={16} />
                    View Details
                </button>
                <button
                    className="venue-btn venue-btn-danger"
                    onClick={() => onRemove(club.clubId)}
                >
                    <X size={16} />
                    Remove
                </button>
            </div>
        </div>
    );
};

const Clubs = () => {
    const navigate = useNavigate();
    const [tab, setTab] = useState(1);
    const [favoriteClubs, setFavoriteClubs] = useState([]);
    const [searchLocation, setSearchLocation] = useState('');
    const [searchClubName, setSearchClubName] = useState('');
    const [discipline, setDiscipline] = useState('');
    const [wadPolicy, setWadPolicy] = useState('');
    const [maxPricePerClay, setMaxPricePerClay] = useState('');
    const [minRifleDistance, setMinRifleDistance] = useState('');
    const [hasElectronicTargets, setHasElectronicTargets] = useState(false);
    const [requiresNonToxicShot, setRequiresNonToxicShot] = useState(false);
    const [dogsAllowed, setDogsAllowed] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [mapData, setMapData] = useState(null);
    const [selectedMarker, setSelectedMarker] = useState(null);

    const handleTabChange = (tabNumber) => {
        setTab(tabNumber);
        if (tabNumber === 2) {
            setSearchResults([]);
            setMapData(null);
            setSelectedMarker(null);
            setHasSearched(false);
        }
    };

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: "AIzaSyDu74EMNNqnvTVsAoz-375ia0BLH0A3HkU"
    });

    const mapContainerStyle = {
        width: '100%',
        height: '400px',
        borderRadius: '16px',
        marginBottom: '24px'
    };

    const fetchFavoriteClubs = useCallback(async () => {
        setError(null);
        setIsLoading(true);
        try {
            const data = await clubsService.getFavoriteClubs();
            setFavoriteClubs(data);
        } catch (error) {
            console.error('Error fetching favorite venues:', error);
            setError('Failed to load favorite venues. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFavoriteClubs();
    }, [fetchFavoriteClubs]);

    const handleSearch = async () => {
        if (!searchLocation && !searchClubName) {
            setError("Please enter a location or venue name");
            return;
        }

        setHasSearched(true);
        setError(null);
        setIsLoading(true);
        setMapData(null);

        try {
            const params = new URLSearchParams();

            if (searchLocation) {
                const formattedLocation = searchLocation.trim();
                params.append('location', formattedLocation + (formattedLocation.toLowerCase().includes('uk') ? '' : ', UK'));
            }

            if (searchClubName) {
                params.append('clubName', searchClubName.trim());
            }
            if (discipline) params.append('discipline', discipline);
            if (wadPolicy) params.append('wadPolicy', wadPolicy);
            if (maxPricePerClay) params.append('maxPricePerClay', maxPricePerClay);
            if (minRifleDistance) params.append('minRifleDistance', minRifleDistance);
            if (hasElectronicTargets) params.append('hasElectronicTargets', 'true');
            if (requiresNonToxicShot) params.append('requiresNonToxicShot', 'true');
            if (dogsAllowed) params.append('dogsAllowed', 'true');

            const data = await clubsService.advancedSearch(params);
            setSearchResults(data);

            if (data.length > 0 && data[0].latitude && data[0].longitude) {
                setMapData({
                    center: {
                        lat: data[0].latitude,
                        lng: data[0].longitude
                    },
                    markers: data.map(club => ({
                        lat: club.latitude,
                        lng: club.longitude,
                        name: club.name,
                        address: club.address,
                        clubId: club.clubId
                    }))
                });
            } else if (data.length === 0) {
                setError("No venues found matching your search. Try a different search term or location.");
            }
        } catch (error) {
            console.error('Error searching for venues:', error);
            setError(error.message || 'Failed to search for venues. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddToFavorites = async (clubId) => {
        setError(null);
        try {
            await clubsService.addFavoriteClub(clubId);
            await fetchFavoriteClubs();
        } catch (error) {
            console.error('Error adding venue to favorites:', error);
            setError('Failed to add venue to favorites. Please try again.');
        }
    };

    const handleRemoveFavorite = async (clubId) => {
        setError(null);
        try {
            await clubsService.removeFavoriteClub(clubId);
            await fetchFavoriteClubs();
        } catch (error) {
            console.error('Error removing venue from favorites:', error);
            setError('Failed to remove venue from favorites. Please try again.');
        }
    };

    const clearSearch = () => {
        setSearchLocation('');
        setSearchClubName('');
        setDiscipline('');
        setWadPolicy('');
        setMaxPricePerClay('');
        setMinRifleDistance('');
        setHasElectronicTargets(false);
        setRequiresNonToxicShot(false);
        setDogsAllowed(false);
        setSearchResults([]);
        setMapData(null);
        setSelectedMarker(null);
        setError(null);
        setHasSearched(false);
    };

    if (isLoading && !searchResults.length && !favoriteClubs.length) {
        return (
            <div className="venues-page">
                <StateCard
                    icon={Navigation}
                    title="Loading venues"
                    description="Fetching your favourites and preparing venue search."
                    loading
                />
            </div>
        );
    }

    return (
        <div className="venues-page">
            {/* Page Header */}
            <div className="venues-header">
                <div className="venues-header-content">
                    <h1>Venues</h1>
                    <p>Find and manage your favourite clubs, ranges, estates, and shooting grounds.</p>
                </div>
                <div className="venues-header-stats">
                    <div className="venues-header-stat">
                        <strong>{favoriteClubs.length}</strong>
                        <span>Favourites</span>
                    </div>
                    <div className="venues-header-stat">
                        <strong>{searchResults.length}</strong>
                        <span>Search Results</span>
                    </div>
                    <div className="venues-header-stat">
                        <strong>{mapData?.markers?.length || 0}</strong>
                        <span>On Map</span>
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <StateCard
                    icon={X}
                    title="Venue action needs attention"
                    description={error}
                    tone="error"
                    actionLabel="Dismiss"
                    actionOnClick={() => setError(null)}
                    compact
                />
            )}

            {/* Tab Navigation */}
            <div className="venues-tabs">
                <button
                    className={`venues-tab ${tab === 1 ? 'active' : ''}`}
                    onClick={() => handleTabChange(1)}
                >
                    <Search size={18} />
                    Find Venues
                </button>
                <button
                    className={`venues-tab ${tab === 2 ? 'active' : ''}`}
                    onClick={() => handleTabChange(2)}
                >
                    <Heart size={18} />
                    Favorites
                    {favoriteClubs.length > 0 && (
                        <span className="venues-tab-badge">{favoriteClubs.length}</span>
                    )}
                </button>
            </div>

            {/* Find Venues Tab */}
            {tab === 1 && (
                <div className="venues-search-section">
                    {!hasSearched && !searchResults.length && !mapData && (
                        <div className="venues-search-callout">
                            <StateCard
                                icon={MapPin}
                                title="Search for venues"
                                description="Enter a location or venue name to find clubs, ranges, and shooting grounds near you."
                                compact
                            />
                        </div>
                    )}
                    <div className="venues-search-card">
                        <div className="venues-search-intro">
                            <h3>Find venues fast</h3>
                            <p>Search by place, venue name, or both, then save favourites for one-tap access.</p>
                        </div>
                        <div className="venues-search-grid">
                            <div className="venues-search-field">
                                <label>Location</label>
                                <div className="venues-input-wrapper">
                                    <Navigation size={18} />
                                    <input
                                        type="text"
                                        placeholder="Enter postcode or location"
                                        value={searchLocation}
                                        onChange={(e) => setSearchLocation(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    />
                                </div>
                            </div>
                            <div className="venues-search-field">
                                <label>Venue Name</label>
                                <div className="venues-input-wrapper">
                                    <Search size={18} />
                                    <input
                                        type="text"
                                        placeholder="Enter venue name"
                                        value={searchClubName}
                                        onChange={(e) => setSearchClubName(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    />
                                </div>
                            </div>
                            <button
                                className="venues-search-btn"
                                onClick={handleSearch}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <div className="btn-spinner"></div>
                                        Searching...
                                    </>
                                ) : (
                                    <>
                                        <Search size={18} />
                                        Search Venues
                                    </>
                                )}
                            </button>
                            <div className="venues-search-field">
                                <label>Discipline</label>
                                <div className="venues-input-wrapper">
                                    <select value={discipline} onChange={(e) => setDiscipline(e.target.value)}>
                                        <option value="">All disciplines</option>
                                        <option value="Clay">Clay</option>
                                        <option value="Rifle">Rifle</option>
                                        <option value="Game">Game</option>
                                        <option value="Mixed">Mixed</option>
                                    </select>
                                </div>
                            </div>
                            <div className="venues-search-field">
                                <label>Wad Policy</label>
                                <div className="venues-input-wrapper">
                                    <select value={wadPolicy} onChange={(e) => setWadPolicy(e.target.value)}>
                                        <option value="">Any</option>
                                        <option value="FibreOnly">Fibre only</option>
                                        <option value="PlasticOnly">Plastic only</option>
                                        <option value="Both">Both</option>
                                    </select>
                                </div>
                            </div>
                            <div className="venues-search-field">
                                <label>Max GBP per clay</label>
                                <div className="venues-input-wrapper">
                                    <input type="number" value={maxPricePerClay} onChange={(e) => setMaxPricePerClay(e.target.value)} />
                                </div>
                            </div>
                            <div className="venues-search-field">
                                <label>Min rifle distance</label>
                                <div className="venues-input-wrapper">
                                    <input type="number" value={minRifleDistance} onChange={(e) => setMinRifleDistance(e.target.value)} />
                                </div>
                            </div>
                            <div className="venues-search-field venues-search-field-flags">
                                <label className="venues-flag">
                                    <input type="checkbox" checked={hasElectronicTargets} onChange={(e) => setHasElectronicTargets(e.target.checked)} />
                                    Electronic targets
                                </label>
                                <label className="venues-flag">
                                    <input type="checkbox" checked={requiresNonToxicShot} onChange={(e) => setRequiresNonToxicShot(e.target.checked)} />
                                    Non-toxic shot
                                </label>
                                <label className="venues-flag">
                                    <input type="checkbox" checked={dogsAllowed} onChange={(e) => setDogsAllowed(e.target.checked)} />
                                    Dogs allowed
                                </label>
                            </div>
                        </div>
                        <div className="venues-search-foot">
                            <button className="venues-search-clear" onClick={clearSearch} disabled={isLoading}>
                                <RefreshCw size={14} />
                                Reset search
                            </button>
                            <button
                                className="venues-search-clear venues-search-secondary"
                                onClick={() => navigate('/dashboard/subscription')}
                            >
                                <Building2 size={14} />
                                Claim or add your venue
                            </button>
                        </div>
                    </div>

                    {/* Map */}
                    {mapData && isLoaded && (
                        <div className="venues-map-container">
                            <GoogleMap
                                mapContainerStyle={mapContainerStyle}
                                center={mapData.center}
                                zoom={10}
                            >
                                {mapData.markers.map((marker, index) => (
                                    <Marker
                                        key={index}
                                        position={{ lat: marker.lat, lng: marker.lng }}
                                        title={marker.name}
                                        onClick={() => setSelectedMarker(marker)}
                                    />
                                ))}

                                {selectedMarker && (
                                    <InfoWindow
                                        position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
                                        onCloseClick={() => setSelectedMarker(null)}
                                    >
                                        <div className="venues-map-info">
                                            <h4>{selectedMarker.name}</h4>
                                            <p>{selectedMarker.address}</p>
                                            <div className="venues-map-info-actions">
                                                <button
                                                    onClick={() => {
                                                        setSelectedMarker(null);
                                                        navigate(`/dashboard/clubs/${selectedMarker.clubId}`);
                                                    }}
                                                >
                                                    View Details
                                                </button>
                                                <button
                                                    className={favoriteClubs.some(fc => fc.clubId === selectedMarker.clubId) ? 'favorited' : ''}
                                                    onClick={() => {
                                                        handleAddToFavorites(selectedMarker.clubId);
                                                        setSelectedMarker(null);
                                                    }}
                                                    disabled={favoriteClubs.some(fc => fc.clubId === selectedMarker.clubId)}
                                                >
                                                    {favoriteClubs.some(fc => fc.clubId === selectedMarker.clubId)
                                                        ? 'In Favorites'
                                                        : 'Add to Favorites'}
                                                </button>
                                            </div>
                                        </div>
                                    </InfoWindow>
                                )}
                            </GoogleMap>
                        </div>
                    )}

                    {/* Search Results */}
                    {searchResults.length > 0 && (
                        <div className="venues-results">
                            <h3 className="venues-results-title">
                                Found {searchResults.length} venue{searchResults.length !== 1 ? 's' : ''}
                            </h3>
                            <div className="venues-grid">
                                {searchResults.map(club => (
                                    <div key={club.clubId} className="venue-card">
                                        <div className="venue-card-header">
                                            <div className="venue-card-icon">
                                                <MapPin size={20} />
                                            </div>
                                        </div>
                                        <h3 className="venue-card-title">{club.name}</h3>
                                        <p className="venue-card-address">
                                            <MapPin size={14} />
                                            {club.address}
                                        </p>
                                        {club.distance ? (
                                            <p className="venue-card-distance">
                                                <Compass size={13} />
                                                Approx. {club.distance} km away
                                            </p>
                                        ) : null}
                                        <div className="venue-card-actions">
                                            <button
                                                className="venue-btn venue-btn-outline"
                                                onClick={() => navigate(`/dashboard/clubs/${club.clubId}`)}
                                            >
                                                <ExternalLink size={16} />
                                                View Details
                                            </button>
                                            <button
                                                className={`venue-btn ${favoriteClubs.some(fc => fc.clubId === club.clubId) ? 'venue-btn-success' : 'venue-btn-primary'}`}
                                                onClick={() => handleAddToFavorites(club.clubId)}
                                                disabled={favoriteClubs.some(fc => fc.clubId === club.clubId)}
                                            >
                                                <Heart size={16} />
                                                {favoriteClubs.some(fc => fc.clubId === club.clubId)
                                                    ? 'In Favorites'
                                                    : 'Add to Favorites'}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Empty State */}
                    {hasSearched && !searchResults.length && !mapData && (
                        <StateCard
                            icon={MapPin}
                            title="No venues matched this search"
                            description="Try broadening your location or leaving one field blank to widen results."
                            compact
                        />
                    )}
                </div>
            )}

            {/* Favorites Tab */}
            {tab === 2 && (
                <div className="venues-favorites-section">
                    {favoriteClubs.length > 0 ? (
                        <div className="venues-grid">
                            {favoriteClubs.map(club => (
                                <FavouriteClubCard
                                    key={club.clubId}
                                    club={club}
                                    onRemove={handleRemoveFavorite}
                                />
                            ))}
                        </div>
                    ) : (
                        <StateCard
                            icon={Heart}
                            title="No favourite venues yet"
                            description="Search for venues and save the ones you want quick access to."
                            actionLabel="Find Venues"
                            actionOnClick={() => setTab(1)}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default Clubs;
